import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import { withTranslation } from "react-i18next";
import { debounce, isEmpty } from "lodash";
import { addDays, format } from "date-fns";

import fpAdjustmentGridService from "@plannel/services/fp/fp-adjustment-grid-service";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import PeriodSearchGroup from "@plannel/pages/factory-plan/common/component/PeriodSearchGroup";
import AdjustQtyPopup, { ADJUST_TYPE } from "@plannel/pages/factory-plan/common/popup/AdjustQtyPopup";
import ResourcePlanDetailTab from "@plannel/pages/factory-plan/report/resource-gantt/ResourcePlanDetailTab";
import PsiTab from "@plannel/pages/factory-plan/report/resource-gantt/PsiTab";
import GanttDetailPanel from "@plannel/pages/factory-plan/common/gantt/GanttDetailPanel";
import { formatQty, formatTimeDiff } from "@plannel/pages/factory-plan/common/gantt/ganttFormat";
import { UI } from "@plannel/pages/factory-plan/common/gantt/ganttConstants";
import FpGridSetting, {
  applyColumnOrder, buildDayBucketPivotColumnDefs, DAY_OF_WEEK_CODES, dayOfWeekHeaderClass
} from "@plannel/pages/factory-plan/common/grid";
import CustomDetailHeader from "@plannel/components/aggrid/CustomDetailHeader";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import { DownloadButton } from "@plannel/components/ActionIconButton";
import ColorUtil from "@plannel/pages/utils/color-util";
import dateUtils from "@plannel/utils/date-util";
import numberUtil from "@plannel/utils/number-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old FP_SRH_RANGE.attr01Val(DB 단일 고정값) 이식 — 버전 선택 시 종료일 = 시작일 + N일.
// fp-report-screen-guide.md §A-3c에서 확정된 방식(화면 상수)을 그대로 따른다.
// TODO(MIG-T2): [adjustmentgrid3/AdjustmentGrid (5109)] 조회구간(FP_SRH_RANGE) 세팅의 테넌트별 커스터마이징 미이관.
//   old: loadSettings()가 common/search-option/data로 FP_SRH_RANGE.attr01Val을 DB에서 조회
//   사유: 정적 상수로 전환 — fp-report-screen-guide.md §A-3c/§A-3d에서 결정된 트레이드오프
const SEARCH_ZONE_DAYS = 9;

const PARAM_DATE_FORMAT = "yyyyMMdd";
const DATETIME_DISPLAY_FORMAT = "yyyy-MM-dd HH:mm:ss";

// 좌측 고정(식별) 컬럼 — old fixColumns와 동일. 붙여넣기 대상이 될 수 없는 컬럼이기도 하다.
const FIX_COLUMNS = ["plantCd", "plantNm", "resourceCd", "resourceNm"];

// old adjustmentgrid3.css의 .adjusted-cell 배경. 신규 scss 파일을 만들지 않고 cellStyle로 옮긴다.
const ADJUSTED_CELL_BG = "#fec3c6";
// 잘라내기 표시 — old는 셀마다 `outline: 3px dashed red`라 연속 범위를 자르면 경계마다 점선이 두 겹으로
// 겹치고 내용까지 가렸다. new에서는 **영역 외곽선만** 점선으로 그린다: 셀마다 상/하/좌/우 이웃이
// 잘라낸 셀인지 보고 이웃이 없는 방향에만 선을 넣는다(직사각형이 아니어도, 가운데가 비어도 정확하다).
// 여기에 영역 배경 + 원본 흐림을 더해 "이동 대기"임을 보여준다.
// 색은 보라 계열로 둔다 — 셀 범위 선택(파랑)·보정 셀(분홍 #fec3c6)·품목 색 띠(주황/분홍 계열)와
// 겹치지 않는 유일한 자리다. 파랑을 쓰면 잘라낸 직후 선택 범위와 같은 색이라 둘을 구분할 수 없다.
// 흐림은 0.7 — 더 낮추면 "비활성"으로 읽히는데, 잘라낸 작업은 붙여넣기 전까지 유효한 데이터다.
// (안 비교: docs/fp-frontend-migration/prototypes/adjustment-grid-cut-styles.html)
const CUT_AREA_BG = "rgba(123, 31, 162, 0.12)";
const CUT_AREA_BORDER = "2px dashed #7B1FA2";
const CUT_CELL_OPACITY = 0.7;

const ROW_HEIGHT = 50;
// 작업 셀의 좌우 패딩(balham 기본 12px 대체) — old처럼 내용이 컬럼 폭을 꽉 채우게 한다.
const CELL_PADDING = "2px";

// ── 자원 묶음 구분 (2026-08-25) ────────────────────────────────────────────────
// 한 자원이 여러 행(seq 슬롯)을 차지하고 빈 슬롯 행도 그대로 남아, 자원이 어디서 바뀌는지 알기 어렵다.
// **간격 행이나 행 그룹은 쓸 수 없다** — 잘라내기/붙여넣기가 표시 행 인덱스로 대상을 지정하기 때문이다.
// 그래서 행 클래스와 pinned 셀 스타일만으로 두 신호를 준다:
//   ① 그룹 첫 행의 굵은 경계선 — 자원이 바뀌는 지점을 표시한다
//   ② 자원 코드 칩            — "여기가 묶음의 머리"임을 알린다
// **날짜 셀 배경은 건드리지 않는다** — 그쪽은 이미 보정/잘라내기/선택 세 신호가 쓰고 있어 경쟁하면 안 된다.
// 좌측 pinned 영역 교대 배경(프로토타입의 신호 ②)은 **넣었다가 뺐다**(2026-08-25 사용자 지시) —
// 자원마다 배경이 달라지는 것이 오히려 시선을 끌었다.
// (안 비교: docs/fp-frontend-migration/prototypes/adjustment-grid-resource-grouping.html)
const GROUP_START_KEY = "__groupStart";
const GROUP_START_CLASS = "fp-adj-group-start";
const GROUP_BORDER = "2px solid #cfd8dc";

// ── 하단 분할 상세 영역 — old DetailsSplitArea(react-split) + TabContainer 이식 ────────────
// 구성·상수·드래그 규칙은 자원 간트(report/ResourceGantt.js §7-14)와 동일하게 맞춘다. 공용
// `SplitterContainer`를 쓰지 않는 이유도 같다(그 화면 주석 참고) — 여기서는 두 화면이 같아 보이도록
// 값만 그대로 가져온다. old AdjustmentGrid3의 탭은 "생산 계획 상세 / PSI" 둘이라 자원 간트의
// 부족 자재(shortage) 탭은 넣지 않는다(이 화면 응답에 해당 데이터가 없다).
const DETAIL_TABS = [
  { key: "plan", labelKey: "FP_RESOURCE_PLAN_DETAILS" },
  { key: "psi", labelKey: "PSI" }
];
const DETAIL_TAB_BAR_HEIGHT = 38;    // 탭 바 높이. 접힘 = 하단 영역이 이 높이만 남은 상태다
const DETAIL_OPEN_HEIGHT = 340;      // 화살표로 펼칠 때의 높이(직전 드래그 높이를 복원하지 않는다)
const DETAIL_MAX_OPEN = 720;         // 드래그 상한 — 그리드가 최소한 남도록
const DETAIL_GUTTER = 10;            // 분할 바 두께(SplitterContainer 기본값과 동일)
const DETAIL_GUTTER_MARGIN = 4;      // 분할 바 위(그리드)·아래(탭 바) 여백
const DETAIL_CONTENT_GAP = 6;        // 탭 바와 그리드 사이 여백(접힘 상태에서는 주지 않는다)
const DETAIL_COLLAPSE_THRESHOLD = DETAIL_TAB_BAR_HEIGHT + 45;
// new 표준은 그리드에 세로 구분선을 두지 않는다. 대신 작업 내용을 옅은 배경 블록("칩")으로 묶어서
// 셀마다 덩어리를 만든다. 반투명이라 아래의 보정/선택 표시가 그대로 비친다.
const CELL_CHIP_BG = "rgba(0, 0, 0, 0.04)";

// 컨텍스트 메뉴 아이콘. AG Grid는 MenuItemDef.icon이 문자열이면 innerHTML로 넣으므로(엔터프라이즈
// AgMenuItemRenderer.addIcon) React 엘리먼트가 아니라 인라인 SVG 문자열을 줘야 한다. 앱의 다른
// 아이콘과 계열을 맞추려고 MUI 아이콘(Edit / CallSplit)의 path를 그대로 쓰고, 색은 메뉴 글자색을
// 따라가도록 currentColor로 둔다.
const menuIcon = (path) =>
  `<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="${path}"/></svg>`;

// MUI Edit(연필) — 수량을 고쳐 넣는다.
const ICON_ADJUST_QTY = menuIcon("M3 17.25V21h3.75L17.81 9.94l-3.75-3.75zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75z");
// MUI CallSplit(갈라지는 화살표) — 작업 하나를 둘로 나눈다.
const ICON_SPLIT_QTY = menuIcon("m14 4 2.29 2.29-2.88 2.88 1.42 1.42 2.88-2.88L20 10V4zm-4 0H4v6l2.29-2.29 4.71 4.7V20h2v-8.41l-5.29-5.3z");
// MUI InfoOutlined — 작업 상세 정보(읽기 전용).
const ICON_ACTIVITY_DETAIL = menuIcon("M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8");

// ---------------------------------------------------------------------------
// old `adjustmentgrid3/adjustmentUtils.js` 이식 — 그리드 라이브러리와 무관한 순수 함수라 로직을
// 그대로 옮겼다. RealGrid의 `__rowId`/`dataRow`는 AG Grid의 표시 행 인덱스(rowIndex)로 대체.
// 화면 1파일 원칙(fp-frontend.md §0-1)에 따라 별도 유틸 파일을 만들지 않고 여기 둔다.
// ---------------------------------------------------------------------------

// 서버로 보낼 때 제외하는 UI 전용 키(old UI_KEYS).
const UI_KEYS = new Set(["isCut", "dataRow"]);

const omitUiKeys = (obj) => {
  const out = {};
  Object.entries(obj).forEach(([key, value]) => {
    if (!UI_KEYS.has(key)) out[key] = value;
  });
  return out;
};

// 한 (공장, 자원, 날짜) 그룹 안에서 seq를 1부터 다시 매기고, 잘라낸 셀이거나 seq가 바뀐 셀만
// 변경분(patch)으로 모은다(old resequenceAndDiff).
const resequenceAndDiff = (list, patches) => {
  list.forEach((cell, index) => {
    const newSeq = index + 1;
    if (cell.isCut || cell.seq !== newSeq) {
      patches.push({ ...omitUiKeys(cell), seq: newSeq });
    }
  });
};

// 붙여넣기 지점보다 위(행 인덱스가 작은)에 있는 기존 셀 개수 — 삽입 위치가 된다(old countBefore).
const countBefore = (list, targetDataRow) =>
  list.filter(cell => cell.dataRow != null && cell.dataRow < targetDataRow).length;

// (공장, 자원) 이 일치하는 행들에서 지정한 날짜 컬럼들의 셀을 그룹키별로 모은다.
// 잘라낸 셀(isCut)은 "이미 그 자리를 떠난 것"이라 제외한다(old collectCellsByDateForPlantResource).
const collectCellsByDateForPlantResource = (groupMap, rows, dateKeys, plantCd, resourceCd) => {
  rows.forEach((row, dataRow) => {
    if (row.plantCd !== plantCd || row.resourceCd !== resourceCd) return;

    dateKeys.forEach(dateKey => {
      const groupKey = `${plantCd}|${resourceCd}|${dateKey}`;
      if (!groupMap.has(groupKey)) groupMap.set(groupKey, []);

      const cell = row[dateKey];
      if (!cell || cell.isCut) return;

      groupMap.get(groupKey).push({ ...cell, dataRow });
    });
  });
};

// 잘라내기 + 붙여넣기 결과로 seq가 달라지는 셀 전체를 계산한다(old collectChanges).
const collectChanges = (rows, dateKeys, cutCells, cutInfo, pasteInfo) => {
  const { sourcePlantCd, sourceResourceCd } = cutInfo;
  const { targetDateKey, targetDataRow, targetPlantCd, targetResourceCd } = pasteInfo;

  const patches = [];
  const groupMap = new Map();
  let sourceDateKeys = dateKeys;

  // 1. target(붙여넣을 영역) 데이터 수집
  if (sourcePlantCd === targetPlantCd && sourceResourceCd === targetResourceCd) {
    // 1-1. 동일 자원 내 붙여넣기 : dateKey(날짜)만 추가로 수집
    sourceDateKeys = [...new Set([...dateKeys, targetDateKey])];
  } else {
    // 1-2. 다른 자원에 붙여넣기 : target 공장/자원의 해당 날짜 데이터 수집
    collectCellsByDateForPlantResource(groupMap, rows, [targetDateKey], targetPlantCd, targetResourceCd);
  }

  // 2. source(잘라낸 영역) 데이터 수집
  collectCellsByDateForPlantResource(groupMap, rows, sourceDateKeys, sourcePlantCd, sourceResourceCd);

  const targetGroupKey = `${targetPlantCd}|${targetResourceCd}|${targetDateKey}`;
  groupMap.forEach((list, groupKey) => {
    if (groupKey === targetGroupKey) {
      // 붙여넣기가 일어나는 그룹에는 잘라낸 셀들을 클릭한 행 위치에 끼워넣는다.
      list.splice(countBefore(list, targetDataRow), 0, ...cutCells);
    }
    resequenceAndDiff(list, patches);
  });

  return patches;
};

// ---------------------------------------------------------------------------

// 크로스탭 셀 = 작업(activity) 객체. old renderer.callback(html)의 React 판.
// 좌측 4px 색 띠(품목 색) + 품목코드 + 수량 2줄 구성.
//
// old처럼 내용이 셀(=헤더에서 내려오는 세로 구분선) 폭을 꽉 채우게 만든다. 그래서
//  - 셀의 좌우 패딩은 activityCellStyle에서 CELL_PADDING까지 줄이고(AG Grid balham 기본은 12px씩이라
//    내용이 구분선에서 크게 안쪽으로 밀린다),
//  - 색 띠는 배경 gradient가 아니라 별도 요소로 둔다. gradient는 이 Box 높이만큼만 칠해져서 행 높이가
//    내용보다 클 때(ROW_HEIGHT=50) 위쪽에 치우쳐 보였다. 지금은 바깥 flex가 내용 블록을 수직 중앙에
//    놓고, 안쪽 stretch가 띠를 텍스트 두 줄 높이에 맞춘다.
const ActivityCell = ({ value }) => {
  if (!value) return null;

  const color = value.itemDisplayColor;
  const isCut = !!value.isCut;
  return (
    // title 속성(브라우저 기본 툴팁)은 두지 않는다 — AG Grid 커스텀 툴팁(ActivityTooltip)과 겹쳐서
    // 툴팁이 두 개로 보인다. 잘린 품목코드는 커스텀 툴팁 제목에 온전히 들어 있다.
    <Box sx={{ display: "flex", alignItems: "center", width: "100%", height: "100%", overflow: "hidden" }}>
      <Box sx={{
        display: "flex", alignItems: "stretch", gap: "4px", width: "100%", minWidth: 0, lineHeight: 1.2,
        // 잘라낸 셀은 셀 배경(CUT_AREA_BG)이 영역을 칠하므로 칩 배경은 비우고 내용만 흐리게 둔다.
        backgroundColor: isCut ? "transparent" : CELL_CHIP_BG, opacity: isCut ? CUT_CELL_OPACITY : 1,
        borderRadius: "3px", py: "2px", pr: "3px"
      }}>
        <Box sx={{ width: "4px", flexShrink: 0, borderRadius: "3px 0 0 3px", backgroundColor: color || "transparent" }} />
        <Box sx={{ flex: 1, minWidth: 0, textAlign: "right" }}>
          <Box component="span" sx={{ display: "block", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", fontWeight: 600 }}>
            {value.itemCd}
          </Box>
          <Box component="span" sx={{ display: "block", color: "#666" }}>
            {numberUtil.formatNumber(value.qty)}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// 툴팁 섹션 구분선 위/아래 여백. 행 간 여백(2px)보다 커야 묶음이 구분된다.
const TOOLTIP_SECTION_GAP = "9px";

// 크로스탭 셀 툴팁. 공용 CustomTooltip은 문자열 한 덩어리만 <p>로 그려서 라벨/값 정렬이 안 되므로
// 이 화면 전용으로 둔다(공용 파일은 수정하지 않는다 — FpGridSetting과 같은 계열의 화면 전용 확장).
// 내용은 buildTooltip이 이미 번역·포맷까지 끝낸 형태로 넘겨주고, 여기서는 배치만 한다.
const ActivityTooltip = ({ value }) => {
  // buildTooltip이 만든 객체만 그린다. defaultColDef에 걸려 있어 나중에 누가 이 그리드에
  // 문자열 툴팁(headerTooltip 등)을 추가하면 여기로 들어오므로 그때는 그리지 않는다.
  if (!value?.sections) return null;

  const { color, header, title, sections } = value;
  return (
    // 카드 셸(테두리·그림자·라운드)과 밴드 색·글자 크기·값 색을 전부 간트 툴팁과 같은 토큰(UI.tip*)으로
    // 맞춘다 — 두 화면의 툴팁이 같은 것으로 보이게.
    //
    // ⚠️ **그림자는 여기(카드)가 아니라 그리드 래퍼의 sx에서 `.ag-tooltip-custom`에 건다.**
    // 이 컴포넌트는 AG Grid가 만든 래퍼 div(`.ag-popup-child.ag-tooltip-custom`) 안에 렌더되는데,
    // 그 환경에서는 카드에 건 `box-shadow`도 `filter`도 화면에 나오지 않았다(2026-08-25 실측).
    // 간트 툴팁은 MUI Popper가 body에 직접 포털로 붙어 래퍼가 없어 문제가 없다.
    <Paper
      elevation={0}
      sx={{
        // 컨텐츠 너비에 맞추고 줄바꿈하지 않는다(사용자 요청). 라벨/값 두 칸이 모든 행에서 같은 폭으로
        // 정렬되도록 셀을 이 그리드의 직계 자식으로 흘려보낸다(행 래퍼를 두면 정렬이 깨진다).
        display: "grid", gridTemplateColumns: "auto auto", width: "max-content", whiteSpace: "nowrap",
        borderRadius: UI.tipCardRadius, border: UI.tipCardBorder,
        // `overflow: hidden` — 밴드 음영이 카드 라운드 코너 밖으로 새지 않게 한다(간트 툴팁과 동일).
        overflow: "hidden",
        fontSize: UI.tipBodyFontSize, color: UI.tipValueFg
      }}
    >
      <Box sx={{
        gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: "6px",
        px: "12px", py: "6px", backgroundColor: UI.tipBandBg,
        borderBottom: "1px solid", borderColor: "divider"
      }}>
        <Box sx={{ width: "10px", height: "10px", borderRadius: "2px", backgroundColor: color || "#BDBDBD" }} />
        <Box component="span" sx={{ fontSize: UI.tipSmallFontSize, fontWeight: 600, color: "text.secondary" }}>
          {header}
        </Box>
      </Box>

      <Box sx={{
        gridColumn: "1 / -1", px: "12px", pt: "8px", pb: "6px",
        fontSize: UI.tipCodeFontSize, fontWeight: 700, letterSpacing: "-0.01em", lineHeight: 1.3
      }}>
        {title}
      </Box>

      {sections.flatMap((rows, sectionIndex) => rows.map((row, rowIndex) => {
        // 섹션 사이에만 구분선을 두고, 그 위/아래 여백을 같게 준다 — 섹션 첫 행의 pt와 마지막 행의
        // pb를 함께 주지 않으면 구분선 아래만 넓고 위는 좁아 보인다. 마지막 섹션의 pb는 툴팁 하단
        // 여백도 겸한다.
        const divider = sectionIndex > 0 && rowIndex === 0;
        const sectionLast = rowIndex === rows.length - 1;
        const cellSx = {
          lineHeight: 1.7,
          borderTop: divider ? "1px solid #EFEFEF" : "none",
          pt: divider ? TOOLTIP_SECTION_GAP : "2px",
          pb: sectionLast ? TOOLTIP_SECTION_GAP : "2px"
        };
        return [
          <Box key={`${row.label}-label`} component="span" sx={{ ...cellSx, pl: "12px", pr: "24px", color: "text.secondary" }}>
            {row.label}
          </Box>,
          <Box key={`${row.label}-text`} component="span" sx={{ ...cellSx, pr: "12px" }}>
            {row.text}
          </Box>
        ];
      }))}
    </Paper>
  );
};

// 잘라낸 영역의 외곽선 — 이웃(위/아래/왼쪽/오른쪽)이 잘라낸 셀이면 그 방향엔 선을 긋지 않는다.
// 그래서 영역 안쪽 경계는 사라지고 바깥 테두리만 남는다. bucketColIds는 화면에 그려진 날짜 컬럼의
// 순서 배열이라 좌우 이웃을 인덱스로 찾을 수 있다.
const cutAreaBorders = (params, bucketColIds) => {
  const rowIndex = params.node?.rowIndex;
  const colIndex = bucketColIds.indexOf(params.column?.getColId());
  if (rowIndex == null || colIndex < 0) return {};

  const isCutAt = (row, col) => {
    if (col < 0 || col >= bucketColIds.length) return false;
    const rowData = params.api?.getDisplayedRowAtIndex(row)?.data;
    return !!rowData?.[bucketColIds[col]]?.isCut;
  };

  // 이웃이 있는 방향은 ""로 둔다("none"이면 인라인 스타일이 남아 셀 선택 테두리를 덮는다).
  return {
    borderTop: isCutAt(rowIndex - 1, colIndex) ? "" : CUT_AREA_BORDER,
    borderBottom: isCutAt(rowIndex + 1, colIndex) ? "" : CUT_AREA_BORDER,
    borderLeft: isCutAt(rowIndex, colIndex - 1) ? "" : CUT_AREA_BORDER,
    borderRight: isCutAt(rowIndex, colIndex + 1) ? "" : CUT_AREA_BORDER
  };
};

// old styleCallback(cut-cell / adjusted-cell) 대응. 상태가 바뀌었을 때 이전 스타일이 남지 않도록
// 항상 모든 속성을 명시해서 반환한다(빈 문자열/none = 해제).
const activityCellStyle = (params, bucketColIds = []) => {
  const isCut = !!params.value?.isCut;
  const isAdjusted = params.value?.attr01 === "Y";

  return {
    // 셀 좌우 패딩 — balham 기본(12px)을 쓰면 내용이 컬럼 폭 안쪽으로 크게 밀려 old와 다르게 보인다.
    paddingLeft: CELL_PADDING,
    paddingRight: CELL_PADDING,
    backgroundColor: isCut ? CUT_AREA_BG : (isAdjusted ? ADJUSTED_CELL_BG : ""),
    // ⚠️ 해제는 반드시 빈 문자열로 한다. "none"으로 쓰면 인라인 스타일이 남아서 balham의
    // `.ag-cell-focus`/`.ag-cell-range-selected` 테두리를 덮어버려 **셀 선택 표시가 사라진다**.
    borderTop: "", borderBottom: "", borderLeft: "", borderRight: "",
    ...(isCut ? cutAreaBorders(params, bucketColIds) : {})
  };
};

const toParamDate = (date) => (date ? format(date, PARAM_DATE_FORMAT) : null);

// 표시 순서 그대로의 행 데이터 배열 — 인덱스가 곧 old의 dataRow다.
// (이 그리드는 정렬/필터를 쓰지 않으므로 표시 인덱스 == 데이터 순서다.)
const getAllRows = (api) => {
  const rows = [];
  const count = api.getDisplayedRowCount();
  for (let index = 0; index < count; index += 1) {
    const data = api.getDisplayedRowAtIndex(index)?.data;
    if (data) rows.push(data);
  }
  return rows;
};

// 일버킷 피벗 컬럼의 요일 헤더 클래스 — crosstabHeader("2026-08-24_MON")에서 요일 코드를 떼어
// 공용 규칙(토=파랑/일=빨강)을 적용하고, **모든 요일에 우측 정렬 클래스를 보장**한다.
// 공용 헬퍼는 평일에 undefined를 돌려주는데(그쪽은 컬럼 type이 정렬을 맡는다) 이 화면은 type이 없다.
const dayHeaderClass = (bucketHeader) => {
  const dayCode = typeof bucketHeader === "string" ? bucketHeader.split("_")[1] : null;
  if (!dayCode || !DAY_OF_WEEK_CODES.has(dayCode)) return undefined;

  return dayOfWeekHeaderClass(dayCode) ?? ["ag-right-aligned-header"];
};

// 한 행에서 작업 객체인 셀만 추려낸다(old `Object.values(row).filter(typeof === 'object')`).
// 고정 컬럼 값은 문자열이라 자연히 걸러진다.
const getActivityCellsOfRow = (row) =>
  Object.values(row).filter(value => typeof value === "object" && !!value && !Array.isArray(value));

// 자원 컬럼의 세로 병합 판정 — old mergeRule "prevvalues + value"(상위 컬럼도 같아야 병합) 대응.
// nodeA/nodeB는 nullable(SpanRowsParams)이라 널 가드 없이 접근하면 조회 시 TypeError가 난다.
const spanByPlantResource = ({ nodeA, nodeB }) =>
  !!nodeA?.data && !!nodeB?.data &&
  nodeA.data.plantCd === nodeB.data.plantCd &&
  nodeA.data.resourceCd === nodeB.data.resourceCd;

// 좌측 pinned 식별 컬럼(공장/자원) 셀 — 병합(spanRows) 셀의 값을 **상단에 붙인다**.
// 세로 중앙 정렬은 묶음이 길어질수록 값이 화면 밖으로 밀려 안 보인다(2026-08-25 사용자 지적).
// 상단 정렬이면 적어도 묶음이 시작될 때 값이 경계선 바로 아래에 온다.
const fixedCellStyle = () => ({
  display: "flex", alignItems: "flex-start", paddingTop: "6px"
});

// 자원 코드 셀 — 값을 칩으로 감싼다(신호 ③). 앱 primary(#048BA8) 계열 옅은 배경이라 상태색
// (보정 분홍 / 잘라내기 보라 / 선택 파랑)과 겹치지 않고, 좌측 pinned 안에만 있어 날짜 영역에 영향이 없다.
const ResourceCodeCell = ({ value }) => {
  if (!value) return null;

  return (
    <Box
      component="span"
      sx={{
        display: "inline-block", fontSize: "11px", fontWeight: 600,
        color: "#0f5b6b", backgroundColor: "rgba(4, 139, 168, 0.10)",
        borderRadius: "10px", px: "8px", py: "1px",
        maxWidth: "100%", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis"
      }}
    >
      {value}
    </Box>
  );
};

const AdjustmentGrid = ({ t, viewName, title }) => {
  const [versionState, setVersionState] = useState({
    planScope: "", planningDate: null, versionCd: "", plantCd: [], selectedVersion: null
  });
  const [periodState, setPeriodState] = useState({
    startDate: null, endDate: null, bucketType: "", isEntirePlanPeriod: true
  });

  const [resourceOptions, setResourceOptions] = useState([]);
  const [resourceCds, setResourceCds] = useState([]);
  // 드롭다운이 열려 있는 동안의 임시 선택. 닫을 때 한 번에 커밋한다(ResourceGantt와 동일).
  const [resourceDraft, setResourceDraft] = useState(null);
  const [showDetailTooltip, setShowDetailTooltip] = useState(true);

  const [rowData, setRowData] = useState([]);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  // 하단 상세 영역 — 선택한 셀의 작업이 두 탭의 기준값이다(old setSelectedActivity).
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [detailTab, setDetailTab] = useState(DETAIL_TABS[0].key);
  const [detailHeight, setDetailHeight] = useState(DETAIL_TAB_BAR_HEIGHT);
  const detailDragRef = useRef(null);
  const detailCollapsed = detailHeight < DETAIL_COLLAPSE_THRESHOLD;

  // 작업 상세 정보(컨텍스트 메뉴 → 우측 오버레이 패널). 자원 간트와 같은 컴포넌트·같은 섹션 구성이다.
  const [activityDetailOpen, setActivityDetailOpen] = useState(false);
  const [activityDetailSections, setActivityDetailSections] = useState([]);
  const [activityDetailLoading, setActivityDetailLoading] = useState(false);
  const [openDetailSections, setOpenDetailSections] = useState(() => new Set(["activity", "order"]));
  const activityDetailTokenRef = useRef(0);

  const [qtyPopup, setQtyPopup] = useState({ open: false, type: null, activity: null });
  // 수량 보정/분할 확인 — old는 팝업 안에서 showMessage로 물었지만, new는 확인 Dialog를 화면이 갖는다.
  const [pendingQtyAdjust, setPendingQtyAdjust] = useState(null);

  const gridRef = useRef();
  // 그리드 래퍼 DOM — 스크롤 뷰포트를 찾을 때 쓴다(restoreScroll 주석 참고).
  const gridBoxRef = useRef(null);
  const searchParamsRef = useRef(null);
  const latestReqRef = useRef(0);
  // 보정 후 되돌릴 스크롤 위치 예약 — 새 rowData가 실제로 반영된 시점(onRowDataUpdated)에 소비한다.
  const pendingScrollRef = useRef(null);

  // 잘라내기 상태. old의 모듈 전역 `lastCutData`를 화면 인스턴스 ref로 격리한다.
  //  - cutBufferRef: 붙여넣기에 쓸 페이로드(원본 객체의 복사본) + 잘라낸 자원 정보
  //  - cutCellsRef : 실제로 isCut을 찍은 rowData 안의 셀 객체들(다음 잘라내기 때 표시를 되돌리는 용도.
  //                  old의 dataProvider.restoreUpdatedRows() 대응)
  const cutBufferRef = useRef(null);
  const cutCellsRef = useRef([]);

  // 공정별 대체 자원 맵(old 모듈 전역 altResources) — 붙여넣기 대상 자원 검증에 쓴다.
  const altResourcesRef = useRef({});
  // 자원 멀티셀렉트에서 마지막으로 "조회에 반영된" 선택값 — 드롭다운을 닫을 때 실제로 바뀌었는지 비교한다.
  const committedResourceCdsRef = useRef([]);
  // 조회 시점의 (공장/자원) 그룹 순서와 그룹별 행 — 보정 결과를 원래 순서로 되꽂을 때 쓴다
  // (old 모듈 전역 plantResourceGroup / dataGroupMap).
  const plantResourceGroupRef = useRef([]);
  const dataGroupMapRef = useRef(null);
  // 현재 그려진 날짜 버킷 컬럼의 순서(= crosstabHeader). 잘라낸 영역의 좌우 이웃을 찾는 데 쓴다.
  const bucketColIdsRef = useRef([]);

  const showDetailTooltipRef = useRef(showDetailTooltip);
  showDetailTooltipRef.current = showDetailTooltip;

  // 체크박스를 껐다 켜면 이미 그려진 셀의 툴팁도 즉시 따라와야 한다. AG Grid는 tooltipValueGetter를
  // 마우스를 올릴 때마다 호출하지 않고 **셀 refresh 시점에 한 번 호출해 캐시**하므로(AgTooltipFeature의
  // updateTooltipText), ref 값만 바꾸면 다음 조회 전까지 옛 상태가 그대로 남는다. force refresh로
  // 셀을 다시 그려 툴팁 값을 재계산시킨다(refreshCell이 항상 tooltipFeature.refreshTooltip을 부른다).
  useEffect(() => {
    gridRef.current?.api?.refreshCells({ force: true, suppressFlash: true });
  }, [showDetailTooltip]);

  const searchStateRef = useRef({ versionState, periodState, resourceCds, resourceOptions });
  searchStateRef.current = { versionState, periodState, resourceCds, resourceOptions };

  const notify = (severity, content) => setSnackInfo({ open: true, severity, content });

  // ── 하단 분할 조작 — 자원 간트(§7-14)와 동일 규칙 ──────────────────────────────
  // 화살표 = 접기/펼치기. 펼칠 때는 직전 드래그 높이를 복원하지 않고 늘 DETAIL_OPEN_HEIGHT로 간다
  // (old handleDrawerOpen이 항상 [70,30]을 넣던 것과 같다). 접어도 선택(selectedActivity)은 유지한다.
  const toggleDetail = useCallback(() => {
    setDetailHeight(prev => (
      prev >= DETAIL_COLLAPSE_THRESHOLD ? DETAIL_TAB_BAR_HEIGHT : DETAIL_OPEN_HEIGHT
    ));
  }, []);

  // 분할 바 드래그 — 위로 끌면 커지므로 부호가 반대다. 놓는 순간 높이로 접힘/펼침을 자동 판정해
  // 화살표 모양이 따라온다(old handleSplitDragEnd의 45px 임계 판정 이식).
  const handleDetailDragStart = useCallback((event) => {
    event.preventDefault();
    detailDragRef.current = { y: event.clientY, height: detailHeight };

    const onMove = (moveEvent) => {
      const drag = detailDragRef.current;
      if (!drag) return;
      setDetailHeight(Math.max(
        DETAIL_TAB_BAR_HEIGHT,
        Math.min(DETAIL_MAX_OPEN, drag.height - (moveEvent.clientY - drag.y))
      ));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      detailDragRef.current = null;
      setDetailHeight(prev => (prev < DETAIL_COLLAPSE_THRESHOLD ? DETAIL_TAB_BAR_HEIGHT : prev));
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [detailHeight]);

  // "생산 계획 상세" 탭 데이터 — old setResourceDetailData 그대로. 선택한 작업과 **같은 (공장,자원)**
  // 행들의 작업 셀을 모아 시작시각 순으로 정렬한다(API 호출 없음 — 조회 응답이 이미 full DTO다).
  // 자원 간트는 `box.activity`에서 꺼내지만 이 화면은 셀 값 자체가 작업 객체라 그대로 쓴다.
  const resourcePlanRows = useMemo(() => {
    if (!selectedActivity) return [];

    const { plantCd, resourceCd } = selectedActivity;
    return rowData
      .filter(row => row.plantCd === plantCd && row.resourceCd === resourceCd)
      .flatMap(getActivityCellsOfRow)
      .sort((a, b) => {
        if (!a.startTs) return 1;
        if (!b.startTs) return -1;
        return new Date(a.startTs) - new Date(b.startTs);
      });
  }, [rowData, selectedActivity]);

  // old onCellClicked — 작업이 있는 셀을 고르면 하단 두 탭의 기준이 되고, 빈 셀/고정 컬럼이면 지운다.
  const handleCellClicked = (params) => {
    const colId = params.column?.getColId();
    const activity = (!colId || FIX_COLUMNS.includes(colId)) ? null : params.value;
    setSelectedActivity(activity ? { ...activity } : null);
  };

  // planScope→plan-dts→planningDate→versions→versionCd/plantCd→기간 재계산 캐스케이드가 여러 렌더에
  // 걸쳐 커밋되면서 각 단계가 독립적으로 조회를 트리거하는 것을 막는다(PlanResult.js와 동일 패턴).
  const debouncedRetrieveRef = useRef();
  if (!debouncedRetrieveRef.current) {
    debouncedRetrieveRef.current = debounce(() => retrieveAdjustments(), 200);
  }
  useEffect(() => () => debouncedRetrieveRef.current.cancel(), []);

  const defaultGridMemo = useMemo(() => {
    const base = FpGridSetting({ title, viewName });
    return {
      ...base,
      // old setNoneEditableGrid + setDisplayOptions({columnMovable:false, columnResizable:false}).
      defaultColDef: {
        ...base.defaultColDef,
        editable: false, sortable: false, resizable: false, suppressMovable: true,
        // tooltipComponent는 그리드 옵션이 아니라 ColDef 속성이다(AG Grid 35 컴포넌트 배치표).
        // gridOptions 최상위에 두면 에러 없이 무시돼 기본 툴팁(문자열 한 줄)이 쓰인다.
        tooltipComponent: ActivityTooltip
      },
      rowHeight: ROW_HEIGHT,
      // 이 화면의 조작 단위는 행이 아니라 **셀**이다(잘라내기/붙여넣기·수량 보정 모두 셀 대상).
      // 공용 DefaultGridSetting은 `rowSelection.enableClickSelection: true`라 셀을 클릭하면 행 전체가
      // 파랗게 잡히는데, 그러면 셀 범위 선택(cellSelection)과 겹쳐 무엇이 대상인지 흐려진다.
      // 행 선택을 쓰는 코드가 이 화면엔 없으므로 통째로 끈다(cellSelection은 base 그대로 유지).
      rowSelection: undefined,
      // 새 rowData가 와도 **맨 위로 튀지 않는다**. 기본값(false)이면 보정 결과가 반영될 때 그리드가
      // 스크롤을 0으로 되돌렸다가 우리가 다시 제자리로 돌려놓게 되어, 그 두 단계가 깜빡임으로 보인다
      // (2026-08-25 사용자 제보). 대신 **조회 때는 맨 위에서 시작해야 하므로** 그 경로에서만 명시적으로
      // 0을 예약한다(retrieveAdjustments의 pendingScrollRef 참고).
      suppressScrollOnNewData: true,
      // 자원이 바뀌는 첫 행에 표식 클래스를 붙인다(신호 ①의 대상). 공용 getRowClass는 편집 상태
      // (updateRow)를 칠하므로 **덮지 않고 합친다** — 이 화면은 전 컬럼 비편집이라 지금은 안 걸리지만,
      // 공용 설정을 조용히 무력화하는 코드를 남기지 않는다.
      getRowClass: (params) => {
        const baseClass = base.getRowClass?.(params);
        const groupClass = params.data?.[GROUP_START_KEY] ? GROUP_START_CLASS : null;
        return [baseClass, groupClass].filter(Boolean).join(" ") || null;
      },
      // old fixColumns의 mergeRule 이식 — 컬럼별 spanRows를 켜기 위한 전역 스위치(CellSpanModule은
      // common/grid.js가 이미 전역 등록해뒀다). 병합 대상은 좌측 pinned 식별 컬럼 4개(FIX_COLUMNS)뿐이고
      // 이 컬럼들은 잘라내기/붙여넣기 대상에서 이미 제외돼 있어, 셀 범위·행 인덱스 로직에는 영향이
      // 없다(렌더링만 바뀐다). cellSelection과 함께 쓰면 AG Grid가 경고를 내지만 동작은 한다 —
      // 근거는 fixedColumnDefs 위 주석 참고.
      enableCellSpan: true,
      // 붙여넣기를 legacy(임시 textarea) 경로로 강제한다. 이 화면은 클립보드 "텍스트"를 쓰지 않고
      // 자체 cut 버퍼로만 동작하는데, 기본 경로인 navigator.clipboard.readText()는 브라우저에
      // 클립보드 읽기 권한 프롬프트를 띄울 수 있다. legacy 경로도 동일하게
      // processDataFromClipboard 콜백에 도달하므로(ag-grid-enterprise clipboardService) 잃는 게 없다.
      suppressClipboardApi: true,
      // 공용 processCellForClipboard는 객체 셀이면 cellRenderer를 호출해 그 결과를 쓰는데, 이 화면의
      // 렌더러는 React 엘리먼트를 돌려주므로 클립보드에 "[object Object]"가 들어간다. 화면 단위로만
      // 덮어써서 사람이 읽을 수 있는 텍스트로 바꾼다(공용 파일은 수정하지 않는다).
      processCellForClipboard: (params) => {
        const value = params.value;
        if (!value || typeof value !== "object") return value ?? "";
        return `${value.itemCd ?? ""} ${numberUtil.formatNumber(value.qty)}`;
      },
      tooltipShowDelay: 300
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // old onShowTooltip — 체크박스가 꺼져 있으면 툴팁을 만들지 않는다(ref로 읽어서 columnDefs 재생성 불필요).
  const buildTooltip = (params) => {
    if (!showDetailTooltipRef.current) return null;

    const value = params.value;
    if (!value) return null;

    const formatTs = (ts) => (ts ? dateUtils.formatDate(ts, DATETIME_DISPLAY_FORMAT) : "");

    // ActivityTooltip이 그대로 그릴 수 있는 형태로 넘긴다. old는 9줄 중 첫 줄이 생산 품목이었는데,
    // 그 자리를 툴팁 제목으로 올리고 나머지를 [생산량] / [시간] / [주문] 세 묶음으로 나눈다.
    return {
      color: value.itemDisplayColor,
      header: t("FP_ACTIVITY"),
      title: `${value.itemCd ?? ""}${value.itemNm ? ` (${value.itemNm})` : ""}`,
      sections: [
        [{ label: t("FP_PRODUCTION_QTY"), text: formatQty(value.qty, value.itemUom) }],
        [
          { label: t("FP_START_TS"), text: formatTs(value.startTs) },
          { label: t("FP_END_TS"), text: formatTs(value.endTs) },
          { label: t("FP_TOTAL_WORK_TIME"), text: diffToWorkTime(value.startTs, value.endTs) }
        ],
        [
          { label: t("FP_WORK_ORDER_CODE"), text: value.woCd ?? "" },
          { label: t("FP_INVENTORY_NM"), text: `${value.woItemCd ?? ""}${value.woItemNm ? ` (${value.woItemNm})` : ""}` },
          { label: t("FP_REQUEST_QTY"), text: formatQty(value.shptQty, value.itemUom) },
          { label: t("FP_DUE_DT"), text: formatTs(value.dueDate) }
        ]
      ]
    };
  };

  // old diffToTimeString + timeToString 조합("H시간 M분 S초"). dateUtils.toTimeString은 ISO 문자열
  // 기반이라 24시간을 넘기면 잘리므로(작업 시간은 넘을 수 있다) 시간 계산은 직접 한다.
  const diffToWorkTime = (startTs, endTs) => {
    if (!startTs || !endTs) return "";

    const seconds = Math.floor((new Date(endTs) - new Date(startTs)) / 1000);
    if (!Number.isFinite(seconds)) return "";

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}${t("FP_HOUR")} ${minutes}${t("FP_MINUTES")} ${(seconds % 3600) % 60}${t("FP_SECOND")}`;
  };

  // 좌측 고정 컬럼 — old gridItems의 접기/펼치기 그룹 2개(FP_PLANT / FP_RESOURCE)를 §0-5 패턴으로.
  // 대표(접힘 시 보이는) 컬럼은 columnGroupShow를 생략하고, 명칭은 "open"으로 둔다.
  //
  // 세로 병합(old mergeRule)은 AG Grid 35의 행 스패닝(`spanRows` + gridOption `enableCellSpan`)으로
  // 옮긴다. 다른 FP 화면 5개(ResourceUtilization·PsiDetail·ToolSettingTab 등)와 같은 방식이다.
  //
  // ⚠️ AG Grid는 `cellSelection`과 함께 쓰면 "colDef.spanRows is not supported with cellSelection"
  // 경고를 낸다(검증은 경고만 내고 옵션을 무시하지는 않는다 — 병합은 그대로 동작한다).
  // **경고를 감수하고 유지하는 이유**: ① 병합 대상이 pinned 식별 컬럼 4개(FIX_COLUMNS)뿐이고 이
  // 컬럼들은 `collectSelectedCells`에서 잘라내기/붙여넣기 대상에서 이미 제외된다 ② 붙여넣기 위치를
  // 정하는 행 인덱스는 렌더링 병합과 무관하다 ③ 같은 조합을 쓰는 FP 화면이 5개 더 있어 이 화면만
  // 다른 방식(표시 생략)을 쓰면 코드·화면이 갈린다(2026-08-25 사용자 결정으로 되돌림).
  // 실제 잘라내기/붙여넣기 동작에서 이상이 확인되면 그때 표시 생략 방식으로 바꾼다.
  //
  // 행 그룹(§A-6b)으로 대체하지 않는 이유: 이 화면의 행은 레코드가 아니라 (공장,자원,seq) 슬롯이고
  // 잘라내기/붙여넣기가 **표시 행 인덱스**로 대상을 지정하는데(handlePasteFromClipboard의
  // focused.rowIndex → collectChanges/countBefore), 행 그룹은 그룹 행을 끼워넣어 그 인덱스 의미를
  // 조용히 깨뜨린다. 게다가 셀 값이 작업 객체라 집계가 성립하지 않고, 그룹을 접으면 붙여넣기 대상
  // 슬롯 자체가 사라진다. 반면 spanRows는 rowData·행 인덱스·버킷 셀 로직을 전혀 건드리지 않는다.
  //
  // 헤더는 Report 화면들과 같은 **1단**으로 둔다. 그 화면들이 1단인 이유는 컬럼 그룹을 아예 쓰지 않고,
  // 코드 컬럼 헤더의 `CustomDetailHeader`(화살표)가 짝이 되는 명칭 컬럼을 보였다/숨겼다 하기 때문이다.
  // 그 헤더 컴포넌트는 `api.setColumnsVisible()`만 호출할 뿐 rowGroup/showRowGroup을 참조하지 않으므로
  // **행 트리와 무관하게** 평범한 field 컬럼에도 그대로 쓸 수 있다(공용 헬퍼 `buildGroupColumnPair`
  // 쪽이 showRowGroup을 써서 트리 전제일 뿐이다 — 이 화면은 위 이유로 트리를 쓸 수 없어 헤더만 빌린다).
  const fixedColumnDefs = useMemo(() => [
    // old mergeRule {criteria:"value"} — 값이 같은 인접 행끼리 병합.
    {
      headerName: "FP_PLANT_CD", field: "plantCd", colId: "plantCd", width: 150, pinned: "left", lockPinned: true,
      spanRows: true, cellStyle: fixedCellStyle,
      headerComponent: CustomDetailHeader,
      headerComponentParams: { viewName, colsHide: true, reduxKey: "plantNmHide", detailColumns: ["plantNm"] }
    },
    { headerName: "PLANT_NM", field: "plantNm", colId: "plantNm", width: 150, pinned: "left", lockPinned: true, hide: true, spanRows: true, cellStyle: fixedCellStyle },
    // old mergeRule {criteria:"prevvalues + value"} — 상위(공장)까지 같을 때만 병합해야 하므로
    // 콜백으로 (공장,자원) 쌍을 비교한다. 다른 공장의 동명 자원이 이어져도 합쳐지지 않는다.
    {
      headerName: "FP_RESOURCE_CD", field: "resourceCd", colId: "resourceCd", width: 150, pinned: "left", lockPinned: true,
      spanRows: spanByPlantResource, cellStyle: fixedCellStyle, cellRenderer: ResourceCodeCell,
      headerComponent: CustomDetailHeader,
      headerComponentParams: { viewName, colsHide: true, reduxKey: "resourceNmHide", detailColumns: ["resourceNm"] }
    },
    { headerName: "FP_RESOURCE_NM", field: "resourceNm", colId: "resourceNm", width: 150, pinned: "left", lockPinned: true, hide: true, spanRows: spanByPlantResource, cellStyle: fixedCellStyle }
    // TODO(MIG-T2): [adjustmentgrid3/AdjustmentGrid (5109)] 컬럼 자동필터(autoFilter) 미이관.
    //   old: plantCd/resourceCd 컬럼에 RealGrid autoFilter
    //   사유: Tier2 표준 유보
    // TODO(MIG-T2): [adjustmentgrid3/AdjustmentGrid (5109)] 가동률 합계 표시(headerSummary/groupFooter) 미이관.
    //   old: plantCd 컬럼에 headerSummary(FP_TOTAL_TARGET_UTILIZATION) + groupFooter(stageCd별 FP_UTILIZATION)
    //   사유: 합계 값을 내려주는 응답 필드가 없고(그리드 응답은 작업 객체뿐) 1차 목표(조회·보정)에 불필요
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, viewName]);

  // 일버킷 피벗 컬럼 — 공용 헬퍼가 만들어주는 숫자 컬럼 기본값을 이 화면용(작업 객체 셀)으로 덮어쓴다
  // (fp-report-screen-guide.md §A-6b의 childExtraProps 오버라이드 경로).
  // ⚠️ childExtraProps의 인자는 **컬럼 하나의 헤더 문자열**("2026-08-24_MON")이고, 바깥 `header`는
  // 그 문자열들의 **배열**이다. 둘을 헷갈려 바깥 것을 쓰면 문자열 API가 없어 여기서 예외가 나고,
  // 호출부의 catch가 그걸 삼켜 "데이터는 오는데 그리드가 빈" 상태가 된다.
  const buildPivotColumnDefs = (header) => buildDayBucketPivotColumnDefs(header, t, {
    childExtraProps: (bucketHeader) => ({
      // old는 85였으나 품목코드가 대부분 말줄임(…)으로 잘려서 넓힌다(사용자 요청).
      width: 95,
      type: undefined,          // 셀 값이 숫자가 아니라 작업 객체라 numberColumn 계열을 쓰지 않는다
      // ⚠️ 위에서 type을 비웠기 때문에 **요일 헤더 정렬을 여기서 직접 챙겨야 한다.**
      // 공용 헬퍼(buildDayBucketPivotColumnDefs)는 토/일에만 headerClass를 명시하고 평일은
      // type의 `rightAligned`가 주는 `ag-right-aligned-header`에 맡기는데(common/grid.js의 해당 주석),
      // 이 화면은 그 type이 없어 **평일 헤더만 좌측 정렬로 남는다**(2026-08-25 사용자 지적).
      // 다른 Report 화면처럼 요일 헤더를 전부 우측 정렬로 통일한다 — 토/일은 공용 헬퍼가 주는
      // 색 클래스(blue-text/red-text)를 그대로 쓰고, 평일은 정렬 클래스만 붙인다.
      headerClass: dayHeaderClass(bucketHeader),
      // 셀 값이 작업 객체라 AG Grid가 데이터 타입을 "object"로 추론하고, 그러면 짝이 되는
      // valueFormatter를 요구한다(warning #48). 이 컬럼은 표시=cellRenderer, 클립보드=
      // processCellForClipboard, 엑셀=processCellCallback으로 각각 따로 처리하므로 타입 추론 자체가
      // 필요 없다. false로 꺼서 formatter/parser/filter 추론을 전부 끊는다.
      cellDataType: false,
      editable: false,
      cellRenderer: ActivityCell,
      cellStyle: (params) => activityCellStyle(params, bucketColIdsRef.current),
      tooltipValueGetter: buildTooltip
    })
  });

  // old setCrossTabGridDataWithBucket — 고정 컬럼 + pivotData[i]를 header[i] 필드로 펼친다.
  //
  // 여기서 **자원 묶음 표시용 플래그**를 같이 계산해 넣는다(§자원 그룹 구분, 2026-08-25):
  //   GROUP_START_KEY — (공장,자원)이 바뀌는 첫 행. 그 행 위에 굵은 경계선을 긋는다.
  // 값이 아니라 **표시용 플래그**라 `__` 접두어를 붙였고, 서버로 나가는 건 셀(작업 객체)뿐이라
  // 이 키는 전송되지 않는다. `getActivityCellsOfRow`도 객체 값만 추리므로 boolean은 자연히 걸러진다.
  const buildRowData = (header, data) => {
    let prevGroupKey = null;

    return (data ?? []).map(row => {
      const groupKey = `${row.plantCd}|${row.resourceCd}`;
      const isGroupStart = groupKey !== prevGroupKey;
      prevGroupKey = groupKey;

      const flat = {
        plantCd: row.plantCd, plantNm: row.plantNm,
        resourceCd: row.resourceCd, resourceNm: row.resourceNm,
        [GROUP_START_KEY]: isGroupStart
      };
      (header ?? []).forEach((key, index) => {
        flat[key] = row.pivotData?.[index] ?? null;
      });
      return flat;
    });
  };

  const clearCutState = () => {
    cutCellsRef.current.forEach(cell => { delete cell.isCut; });
    cutCellsRef.current = [];
    cutBufferRef.current = null;
  };

  // 조회 응답/보정 응답을 그리드에 반영한다(old setCrossTabGrid).
  const applyCrossTabData = (header, data) => {
    const api = gridRef.current?.api;
    if (!api) return;

    bucketColIdsRef.current = header ?? [];

    // 조회할 때마다 날짜 버킷 컬럼 구성이 통째로 바뀐다 → columnDefs를 다시 만들어 재정의하고,
    // setColumnDefs가 되돌려놓는 "직전 조회의 컬럼 순서"를 header 순서로 교정한다(§A-6c).
    const nextColumnDefs = [...fixedColumnDefs, ...buildPivotColumnDefs(header)];
    GridUtils.setColumnDefs({ api, columnDefs: nextColumnDefs, viewName, initState: false });
    applyColumnOrder(api, nextColumnDefs);

    setRowData(buildRowData(header, data));
  };

  // old setPlantResourceGroup — 조회 결과의 (공장/자원) 그룹 순서를 기억해 둔다.
  const rememberGroupOrder = (data) => {
    if (isEmpty(data)) {
      plantResourceGroupRef.current = [];
      dataGroupMapRef.current = null;
      return;
    }

    plantResourceGroupRef.current = [...new Set(data.map(row => `${row.plantCd}/${row.resourceCd}`))];
    dataGroupMapRef.current = data.reduce((map, row) => {
      const key = `${row.plantCd}/${row.resourceCd}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
      return map;
    }, new Map());
  };

  // old makeArrangedJsonData — 보정 응답은 변경된 그룹만 담고 있으므로, 조회 시점의 그룹 순서에
  // 그 그룹만 갈아끼워 전체 데이터를 다시 만든다.
  const mergeArrangedData = (arrangedRows) => {
    if (isEmpty(plantResourceGroupRef.current) || dataGroupMapRef.current === null) return null;

    const groupMap = dataGroupMapRef.current;
    const changedGroups = [...new Set((arrangedRows ?? []).map(row => `${row.plantCd}/${row.resourceCd}`))];

    changedGroups.forEach(key => groupMap.set(key, []));
    (arrangedRows ?? []).forEach(row => {
      const key = `${row.plantCd}/${row.resourceCd}`;
      if (!groupMap.has(key)) groupMap.set(key, []);
      groupMap.get(key).push(row);
    });

    return plantResourceGroupRef.current.flatMap(key => groupMap.get(key) ?? []);
  };

  const buildSearchParams = () => {
    const { versionState: version, periodState: period, resourceCds: selected, resourceOptions: options } = searchStateRef.current;

    return {
      viewId: viewName,
      subId: "",
      planScope: version.planScope,
      versionCd: version.versionCd,
      plantCds: version.plantCd,
      // old: 전체 선택이면 빈 배열(= 서버에서 조건 생략)
      resourceCds: options.length > 0 && options.length === selected.length ? [] : selected,
      fromDt: toParamDate(period.startDate),
      toDt: toParamDate(period.endDate),
      usePlanPeriod: !!period.isEntirePlanPeriod
      // old가 보내던 localeDateFormat은 보내지 않는다 — 백엔드 기본값(Bucket.DEFAULT_DATE_FORMAT)이
      // "yyyy-MM-dd"이고, 이 화면은 그 값이 AdjustmentGridService의 planDt 포맷(DATE_FORMATTER,
      // 역시 "yyyy-MM-dd")과 **같아야** 보정 재정렬(planDt 문자열 정렬)이 깨지지 않는다.
      // 다른 Report 화면들도 전부 미전송으로 통일돼 있다(ItemCycleTime.js 참고).
    };
  };

  const retrieveAdjustments = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;

    if (!version.planScope || !version.versionCd) {
      notify(snackBarMessageColor.inputError, t("FP_MSG_SELECT_VERSION"));
      return;
    }
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) {
      notify(snackBarMessageColor.inputError, t("FP_MSG_SELECT_PLAN_PERIOD"));
      return;
    }

    const reqId = ++latestReqRef.current;
    const params = buildSearchParams();
    searchParamsRef.current = params;

    gridRef.current?.api?.setGridOption("loading", true);

    fpAdjustmentGridService.getAdjustments(params)
      .then(response => {
        if (reqId !== latestReqRef.current) return;

        const { header, data } = response.data ?? {};
        clearCutState();
        rememberGroupOrder(data);
        // `suppressScrollOnNewData: true`라 새 데이터가 와도 스크롤이 그대로 남는다. 보정과 달리
        // **조회는 맨 위에서 시작해야 하므로** 여기서만 0을 예약한다(보정은 현재 위치를 예약한다).
        pendingScrollRef.current = { top: 0, left: 0 };
        applyCrossTabData(header, data);

        if (isEmpty(data)) notify(snackBarMessageColor.dataEmpty, t("MSG_NoData"));
      })
      .catch(e => debug(e))
      .finally(() => {
        // 스크롤 위치가 예약돼 있으면 **복원까지 끝난 뒤**(restoreScroll) 오버레이를 걷는다.
        // 여기서 먼저 걷으면 데이터가 붙고 위치가 잡히는 과정이 사용자에게 그대로 보인다.
        if (reqId !== latestReqRef.current || pendingScrollRef.current) return;
        gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  // old loadAltResources — planScope 단위로 공정별 대체 자원 맵을 미리 받아둔다.
  const retrieveAltResources = (planScope) => {
    if (!planScope) return;

    fpAdjustmentGridService.getAltResources({ planScope })
      .then(response => { altResourcesRef.current = response.data ?? {}; })
      .catch(e => {
        altResourcesRef.current = {};
        debug(e);
      });
  };

  // old loadResources — 자원 멀티셀렉트 옵션을 채우고 전체 선택 상태로 시작한다.
  const retrieveResources = (planScope) => {
    const reset = () => {
      setResourceOptions([]);
      setResourceCds([]);
      setResourceDraft(null);
      committedResourceCdsRef.current = [];
    };

    if (!planScope) {
      reset();
      return;
    }

    fpAdjustmentGridService.getResources({ viewId: viewName, subId: "", planScope, resourceCdNm: "" })
      .then(response => {
        const options = (response.data ?? []).map(resource => ({ value: resource.resourceCd, label: resource.resourceNm }));
        const allCds = options.map(option => option.value);
        setResourceOptions(options);
        setResourceCds(allCds);
        setResourceDraft(null);
        // 전체 선택 상태가 곧 "조회에 반영된" 기준값이다 — 목록을 새로 받은 직후엔 재조회가 필요 없다.
        committedResourceCdsRef.current = allCds;
      })
      .catch(e => {
        reset();
        debug(e);
      });
  };

  // ── 자원 멀티셀렉트 — ResourceGantt(자원 간트)와 동일한 구성으로 맞춘다 ──────────────
  // 요약 표시(전체/‘첫 항목 +N’) · 체크박스 · 모두 선택/전체 해제 · 닫을 때 커밋.
  // 자원이 50~100개라 Chip을 쌓으면 조회조건 줄이 통째로 태그밭이 되고(기본값이 전체 선택이다),
  // 전체 해제/모두 선택을 항목마다 클릭해서 만들어야 하는 문제도 그대로였다.
  // 다른 후보(서버 검색형 ItemAutocomplete / 공용 ResourceFilter)를 왜 안 썼는지는
  // ResourceGantt.js의 같은 자리 주석에 근거가 정리돼 있다.
  const selectedResourceOptions = useMemo(() => {
    if (resourceDraft) return resourceDraft;
    const picked = new Set(resourceCds);
    return resourceOptions.filter(option => picked.has(option.value));
  }, [resourceDraft, resourceOptions, resourceCds]);

  const allResourcesSelected = resourceOptions.length > 0
    && selectedResourceOptions.length === resourceOptions.length;

  // 드롭다운 상단 고정 액션. `onMouseDown` 기본동작을 막지 않으면 버튼을 누르는 순간 입력이
  // 포커스를 잃어 드롭다운이 닫힌다.
  const renderResourcePaper = useCallback(({ children, ...paperProps }) => (
    <Paper {...paperProps}>
      <Box
        sx={{ display: "flex", gap: 0.5, px: 1, py: 0.5, borderBottom: 1, borderColor: "divider" }}
        onMouseDown={(event) => event.preventDefault()}
      >
        <Button size="small" onClick={() => setResourceDraft(resourceOptions)}>{t("selectAll")}</Button>
        <Button size="small" color="inherit" onClick={() => setResourceDraft([])}>{t("clearAll")}</Button>
      </Box>
      {children}
    </Paper>
  ), [resourceOptions, t]);

  // 드롭다운을 닫을 때 draft를 반영하고, 선택이 실제로 바뀐 경우에만 재조회한다.
  // 선택 순서로 들어온 draft를 옵션 순서로 정규화해서 그 비교가 성립하게 만든다.
  const commitResourceSelection = () => {
    const picked = resourceDraft && new Set(resourceDraft.map(option => option.value));
    const next = picked
      ? resourceOptions.filter(option => picked.has(option.value)).map(option => option.value)
      : searchStateRef.current.resourceCds;

    setResourceDraft(null);

    const committed = committedResourceCdsRef.current;
    if (next.length === committed.length && next.every(cd => committed.includes(cd))) return;

    setResourceCds(next);
    committedResourceCdsRef.current = [...next];
    debouncedRetrieveRef.current();
  };

  // old checkAllAltResources/checkAltResources — 잘라낸 작업 전부가 대상 자원을 대체 자원으로 갖는지.
  const isAllAltResources = (cells, targetResourceCd) => cells.every(cell => {
    const alternatives = altResourcesRef.current?.[cell.routeCd];
    return !!alternatives && alternatives.some(resource => resource.resourceCd === targetResourceCd);
  });

  // 선택된 셀 범위에서 작업이 있는 셀만 추린다(old grid.getSelectionsData + groupByDateKey의 값 필터).
  const collectSelectedCells = (api) => {
    const result = [];
    const seen = new Set();

    (api.getCellRanges() ?? []).forEach(range => {
      const startIndex = range.startRow?.rowIndex ?? 0;
      const endIndex = range.endRow?.rowIndex ?? 0;

      for (let rowIndex = Math.min(startIndex, endIndex); rowIndex <= Math.max(startIndex, endIndex); rowIndex += 1) {
        const row = api.getDisplayedRowAtIndex(rowIndex)?.data;
        if (!row) continue;

        range.columns.forEach(column => {
          const colId = column.getColId();
          if (FIX_COLUMNS.includes(colId)) return;

          const key = `${rowIndex}|${colId}`;
          if (seen.has(key)) return;
          seen.add(key);

          const cell = row[colId];
          if (cell) result.push({ rowIndex, colId, cell, row });
        });
      }
    });

    return result;
  };

  // Ctrl+X — AG Grid 내장 cut을 그대로 쓴다. 이 그리드는 전 컬럼 비편집이라 내장 cut의
  // 값 지우기(clearCellValue)가 `column.isCellEditable()` 검사에서 즉시 리턴되므로, old가
  // `onCut`에서 `return false`로 막던 것과 결과가 같다 — 여기서는 표시(isCut)만 입힌다.
  const handleCutStart = () => {
    const api = gridRef.current?.api;
    if (!api) return;

    const selected = collectSelectedCells(api);
    clearCutState();

    if (isEmpty(selected)) {
      api.refreshCells({ force: true });
      return;
    }

    // old groupByDateKey: 서로 다른 자원의 셀이 섞이면 잘라내기 자체를 취소한다.
    if (new Set(selected.map(item => item.row.resourceCd)).size > 1) {
      notify(snackBarMessageColor.inputError, t("FP_MSG_SELECT_SAME_RESOURCE"));
      api.refreshCells({ force: true });
      return;
    }

    const data = {};
    selected.forEach(({ colId, cell }) => {
      cell.isCut = true;
      cutCellsRef.current.push(cell);

      if (!data[colId]) data[colId] = [];
      data[colId].push({ ...cell });
    });

    const first = selected[0].row;
    cutBufferRef.current = { sourcePlantCd: first.plantCd, sourceResourceCd: first.resourceCd, data };

    api.refreshCells({ force: true });
  };

  // Ctrl+V — 내장 붙여넣기 파이프라인을 "트리거"로만 쓰고 적용은 직접 한다(old onPaste + return false).
  // 클립보드 텍스트는 쓰지 않는다(페이로드는 cutBufferRef) → 항상 null을 반환해 내장 붙여넣기를 취소.
  const handlePasteFromClipboard = () => {
    const api = gridRef.current?.api;
    const cut = cutBufferRef.current;
    const focused = api?.getFocusedCell();

    if (!api || !cut || !focused) return null;

    const targetDateKey = focused.column.getColId();
    if (FIX_COLUMNS.includes(targetDateKey)) return null;

    const targetRow = api.getDisplayedRowAtIndex(focused.rowIndex)?.data;
    if (!targetRow) return null;

    const cutDateKeys = Object.keys(cut.data);
    const cutCells = Object.values(cut.data).flat();
    // 1. 날짜 오름차순 → 2. 같은 날짜면 seq 오름차순 (old 동일)
    cutCells.sort((a, b) => {
      const dtCompare = (a.planDt ?? "").localeCompare(b.planDt ?? "");
      return dtCompare !== 0 ? dtCompare : Number(a.seq) - Number(b.seq);
    });

    if (!isAllAltResources(cutCells, targetRow.resourceCd)) {
      notify(snackBarMessageColor.inputError, t("FP_MSG_ADJUST_ALTER_RESOURCE"));
      return null;
    }

    // 컬럼 id가 "2026-07-21_TUE" 형태라 앞부분이 곧 새 계획일(planDt, "yyyy-MM-dd")이다.
    const newPlanDt = targetDateKey.split("_")[0];
    cutCells.forEach(cell => {
      cell.plantCd = targetRow.plantCd;
      cell.plantNm = targetRow.plantNm;
      cell.resourceCd = targetRow.resourceCd;
      cell.resourceNm = targetRow.resourceNm;
      cell.header = targetDateKey;
      cell.planDt = newPlanDt;
      cell.attr01 = "Y";
    });

    const cutInfo = { sourcePlantCd: cut.sourcePlantCd, sourceResourceCd: cut.sourceResourceCd };
    const pasteInfo = {
      targetDateKey,
      targetDataRow: focused.rowIndex,
      targetPlantCd: targetRow.plantCd,
      targetResourceCd: targetRow.resourceCd
    };

    const changes = collectChanges(getAllRows(api), cutDateKeys, cutCells, cutInfo, pasteInfo);
    reArrange(changes, cutInfo, pasteInfo);

    return null;
  };

  // reArrange 요청 본문 다이어트 — 값이 없는 필드와 서버가 스스로 다시 만드는 파생 필드를 뺀다.
  //
  // 이 요청은 form-urlencoded(`changes` 파라미터)로 나가는데, URL 인코딩이 JSON을 1.5배로 부풀린다.
  // Tomcat `maxPostSize`(기본 2MB)를 넘기면 **예외 없이 파라미터 맵이 통째로 비워져서** 서버에서
  // `getChanges()`가 null이 되고 Jackson이 `argument "content" is null`로 터진다(크기 관련 메시지가
  // 전혀 안 나와서 원인 찾기 어렵다). 자원 2그룹 × 3개월이면 인코딩 후 2.1MB로 이미 한도를 넘는다.
  //
  // - null 필드: AdjustmentsDto는 필드 104개 중 40개가 항상 null(attr02~attr20, 감사 컬럼 등)이다.
  //   JSON에서 빼도 Jackson이 어차피 null로 남겨두므로 서버가 보는 값은 동일하다. 분할로 생긴 신규 행의
  //   `id: null`도 마찬가지 — 서버는 `getId() == null`로 신규를 판별하는데 키가 없어도 null이라 그대로다.
  // - uniqueKey / activityIdToString: DTO의 계산된 getter라 서버가 역직렬화 후 다시 만든다.
  //
  // 서버는 받은 DTO를 그대로 되돌려주므로(getArrangedData가 merge+sort+pivot만 하고 DB를 다시 안 읽는다)
  // **값이 있는 필드는 하나도 뺄 수 없다** — 빼면 보정 후 그 값이 그리드에서 영구히 사라진다.
  const DERIVED_FIELDS = ["uniqueKey", "activityIdToString"];
  const toRequestRows = (rows) => rows.map(row => {
    const trimmed = {};
    Object.entries(row).forEach(([key, value]) => {
      if (value !== null && value !== undefined && !DERIVED_FIELDS.includes(key)) trimmed[key] = value;
    });
    return trimmed;
  });

  // old reArrange — 영향 받는 (공장/자원) 그룹의 원본 전체 + 변경분을 보내고, 서버가 재정렬해
  // 돌려준 결과로 그리드를 다시 그린다.
  const reArrange = (adjustedData, cutInfo, pasteInfo) => {
    const api = gridRef.current?.api;
    if (!api) return;

    const { sourcePlantCd, sourceResourceCd } = cutInfo;
    const targetPlantCd = pasteInfo?.targetPlantCd;
    const targetResourceCd = pasteInfo?.targetResourceCd;

    const originalData = getAllRows(api)
      .filter(row =>
        (row.plantCd === sourcePlantCd && row.resourceCd === sourceResourceCd) ||
        (!!pasteInfo && row.plantCd === targetPlantCd && row.resourceCd === targetResourceCd))
      .flatMap(getActivityCellsOfRow);

    // old getLeftPos/getTopItem — 보정 후 보고 있던 위치로 돌려놓는다.
    // **픽셀 값을 기준으로 잡는다** — 행/컬럼 인덱스만 쓰면 `ensureIndexVisible`이 "그 행이 보이게만"
    // 맞춰서 몇십 px 어긋난다(2026-08-25 사용자 제보: 보정 후 다른 위치가 보인다).
    // 읽기는 공개 API(getVerticalPixelRange/getHorizontalPixelRange)를 쓰고, 되돌리는 것만 DOM으로 한다
    // (AG Grid 35에 스크롤 위치 setter가 공개돼 있지 않다 — 내부도 `eBodyViewport.scrollTop`을 직접 쓴다).
    // 인덱스 값도 같이 담아둔다 — 뷰포트를 못 찾았을 때의 폴백이다.
    const pixel = api.getVerticalPixelRange?.();
    const horizontal = api.getHorizontalPixelRange?.();
    const topRowIndex = api.getFirstDisplayedRowIndex();
    const leftColId = api.getAllDisplayedColumns()
      ?.find(column => !FIX_COLUMNS.includes(column.getColId()) && !column.isPinned())?.getColId();

    gridRef.current?.api?.setGridOption("loading", true);

    fpAdjustmentGridService.getArrangedActivities(
      searchParamsRef.current,
      { original: toRequestRows(originalData), adjusted: toRequestRows(adjustedData) }
    )
      .then(response => {
        const { header, data } = response.data ?? {};
        const merged = mergeArrangedData(data);

        if (!merged) {
          notify(snackBarMessageColor.inputError, t("FP_MSG_ADJUST_FAIL"));
          return;
        }

        clearCutState();

        // ⚠️ **스크롤 복원은 여기서 바로 하면 안 된다.** applyCrossTabData가 부르는 setRowData는
        // React state 갱신이라, 그 자리에서 ensureIndexVisible을 부르면 그리드가 아직 **이전 rowData**를
        // 들고 있는 상태에서 계산된다. 곧이어 새 rowData가 들어오면서 스크롤이 맨 위로 리셋된다
        // (2026-08-25 사용자 제보). common/grid.js의 `consumePendingFocus`가 focus에 대해 같은 레이스를
        // 다루는 것과 같은 구조 — **예약해두고 onRowDataUpdated에서 소비한다.**
        pendingScrollRef.current = {
          top: pixel?.top, left: horizontal?.left, rowIndex: topRowIndex, colId: leftColId
        };
        applyCrossTabData(header, merged);
      })
      .catch(e => {
        notify(snackBarMessageColor.inputError, t("FP_MSG_ADJUST_FAIL"));
        debug(e);
      })
      // 조회와 같은 규칙 — 스크롤 복원이 예약돼 있으면 restoreScroll이 끝나고 오버레이를 걷는다.
      .finally(() => {
        if (pendingScrollRef.current) return;
        gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  // old activityDetails 메뉴 — 선택한 작업의 상세를 우측 패널에 띄운다. 자원 간트의 loadDetail과
  // 같은 흐름이고, 응답 DTO(ActivityDetailDto)도 같아서 **섹션 구성을 그대로 쓴다**(두 화면이 갈라지지
  // 않도록 값 포맷도 같은 유틸 — formatQty/formatTimeDiff/dateUtils).
  const loadActivityDetail = (activity) => {
    const version = searchStateRef.current.versionState;
    if (!activity || !version.planScope || !version.versionCd) return;

    setActivityDetailOpen(true);

    // 여러 셀을 빠르게 열 때 늦게 온 응답이 화면을 덮지 않게 한다(자원 간트와 같은 토큰 패턴).
    const token = ++activityDetailTokenRef.current;
    setActivityDetailLoading(true);

    fpAdjustmentGridService.getAdjustmentsDetail({
      viewId: viewName,
      subId: "",
      planScope: version.planScope,
      versionCd: version.versionCd,
      plantCd: activity.plantCd,
      resourceCd: activity.resourceCd,
      activityId: activity.activityId
    })
      .then(response => {
        // 로딩 해제도 토큰 검사 뒤에 한다 — 뒤처진 응답이 진행 중인 조회의 진행 바를 끄면 안 된다.
        if (token !== activityDetailTokenRef.current) return;
        setActivityDetailLoading(false);

        const detail = response.data;
        if (!detail) return;

        // 코드/명칭은 백엔드가 **각각** 내려준다(2026-08-25 정리 — 그전에는 쿼리에서 "코드 (명칭)"으로
        // 합쳐 보내 좁은 패널에서 아무 데서나 접혔다). `subValue`로 넘기면 "코드 굵게 / 명칭 작게"
        // 두 줄 고정으로 그려진다.
        setActivityDetailSections([
          {
            key: "activity",
            titleKey: "FP_ACTIVITY_INFO",
            rows: [
              { labelKey: "FP_PRODUCTION_ITEM", value: detail.itemCd, subValue: detail.itemNm },
              { labelKey: "FP_QTY", value: formatQty(detail.qty, detail.itemUom) },
              { labelKey: "FP_START_TS", value: dateUtils.formatDateTime(detail.startTs) },
              { labelKey: "FP_END_TS", value: dateUtils.formatDateTime(detail.endTs) },
              { labelKey: "FP_TOTAL_WORK_TIME", value: formatTimeDiff(detail.tsDiff, t) },
              { labelKey: "FP_RESOURCE", value: detail.resourceCd, subValue: detail.resourceNm },
              { labelKey: "FP_ROUTE", value: detail.routeCd, subValue: detail.routeNm },
              // WIP은 기존대로 읽기전용 체크박스를 유지한다(2026-08-25 사용자 지시).
              { labelKey: "FP_WIP", value: Boolean(detail.wipYn), checkbox: true }
            ]
          },
          {
            key: "order",
            titleKey: "FP_ORDER_INFO",
            rows: [
              { labelKey: "FP_WORK_ORDER_CODE", value: detail.woCd },
              { labelKey: "FP_INVENTORY_NM", value: detail.woItemCd, subValue: detail.woItemNm },
              { labelKey: "FP_REQUEST_QTY", value: formatQty(detail.shptQty, detail.itemUom) },
              { labelKey: "FP_START_TS", value: dateUtils.formatDateTime(detail.woStartTs) },
              { labelKey: "FP_END_TS", value: dateUtils.formatDateTime(detail.woEndTs) },
              { labelKey: "FP_TOTAL_PRODUCTION_TIME", value: formatTimeDiff(detail.woTsDiff, t) },
              {
                labelKey: "FP_DUE_DT",
                value: dateUtils.formatDateTime(detail.dueDate),
                ...(detail.lateYn ? { tone: "warn" } : null)
              },
              {
                // 상태 낱말이라 뱃지(둥근 배경)로 — 위 납기일은 값 자체가 데이터라 글자색만 바꾼다.
                labelKey: "FP_ONTIME_DELIVERY",
                value: t(detail.lateYn ? "FP_DELAY" : "FP_ONTIME"),
                tone: detail.lateYn ? "warn" : "ok",
                chip: true
              },
              { labelKey: "FP_PLAN_SEQ", value: detail.planSeq == null ? "" : numberUtil.formatNumber(Number(detail.planSeq)) },
              { labelKey: "FP_SO_CD", value: detail.dmndCd },
              { labelKey: "FP_SALES_ORDER_QTY", value: formatQty(detail.dmndQty, detail.itemUom) },
              { labelKey: "customer", value: detail.customerCd, subValue: detail.customerNm }
            ]
          }
        ]);
      })
      .catch(e => {
        if (token === activityDetailTokenRef.current) setActivityDetailLoading(false);
        debug(e);
      });
  };

  const toggleDetailSection = useCallback((key) => {
    setOpenDetailSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // old onContextMenuPopup/onContextMenuItemClicked — 작업이 있는 셀에서만 활성화된다.
  const getContextMenuItems = (params) => {
    const colId = params.column?.getColId();
    if (!colId || FIX_COLUMNS.includes(colId)) return [];

    const activity = params.value;
    if (!activity) return [];

    const openQtyPopup = (type) => setQtyPopup({ open: true, type, activity });

    // 순서는 old setContextMenu 그대로 — 상세 정보(읽기)가 먼저, 값을 바꾸는 두 항목이 뒤.
    return [
      { name: t("FP_ACTIVITY_DETAIL"), icon: ICON_ACTIVITY_DETAIL, action: () => loadActivityDetail(activity) },
      "separator",
      { name: t("FP_ADJUST_ACTIVITY_QTY"), icon: ICON_ADJUST_QTY, action: () => openQtyPopup(ADJUST_TYPE.ADJUST) },
      { name: t("FP_SPLIT_ACTIVITY_QTY"), icon: ICON_SPLIT_QTY, action: () => openQtyPopup(ADJUST_TYPE.SPLIT) }
    ];
  };

  // old adjustQty — **보정과 분할의 처리가 다르다**(old와 동일, 2026-08-25 사용자 확인):
  //   보정: 대상 셀의 수량만 바꾼다. **서버를 부르지 않는다.**
  //   분할: 행이 하나 늘고 뒤쪽 seq가 밀려 순서가 바뀌므로 reArrange로 재정렬을 받아온다.
  // `grid3/arranged-activities`는 **DB에 저장하지 않고 재정렬 결과만 돌려주는** 계산 API라, 값만 바뀌는
  // 보정에서는 부를 이유가 없다(부르면 왕복 시간과 화면 갱신만 생긴다).
  const applyQtyAdjust = ({ type, activity, data }) => {
    const api = gridRef.current?.api;
    if (!api || !activity) return;

    const { plantCd, resourceCd, seq, header } = activity;
    const cutInfo = { sourcePlantCd: plantCd, sourceResourceCd: resourceCd };

    if (type === ADJUST_TYPE.ADJUST) {
      // old `dataProvider.setValue(dataRow, fieldName, change)` 대응 — rowData 안의 **셀 객체를 직접
      // 고치고** 다시 그린다(잘라내기 표시가 `cell.isCut`을 직접 쓰는 것과 같은 방식).
      activity.qty = data.adjustQty;
      activity.attr01 = "Y";                     // 보정됨 표시 — 셀 배경이 분홍으로 바뀐다
      api.refreshCells({ force: true, suppressFlash: true });

      // 하단 "생산 계획 상세" 탭은 selectedActivity를 의존성으로 다시 계산한다. 셀 객체를 제자리에서
      // 고쳤을 뿐이라 참조가 그대로여서 갱신되지 않으므로, 같은 값의 새 참조를 넣어 재계산시킨다.
      setSelectedActivity(prev => (prev && prev.activityId === activity.activityId ? { ...activity } : prev));
      return;
    }

    const splitList = [
      { ...omitUiKeys(activity), qty: data.remainingQty, attr01: "Y" },
      { ...omitUiKeys(activity), id: null, qty: data.splitQty, seq: Number(seq) + 1, attr01: "Y" }
    ];

    // 분할 대상보다 seq가 뒤인 같은 (공장/자원/날짜) 작업들의 seq를 1씩 민다.
    const reOrderList = [];
    getAllRows(api).forEach(row => {
      if (row.plantCd !== plantCd || row.resourceCd !== resourceCd) return;

      const cell = row[header];
      if (cell && Number(seq) < Number(cell.seq)) {
        reOrderList.push({ ...omitUiKeys(cell), seq: Number(cell.seq) + 1 });
      }
    });

    reArrange([...splitList, ...reOrderList], cutInfo, null);
  };

  const onGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs: fixedColumnDefs, viewName, initState: false });
  };

  // 예약된 스크롤 위치를 되돌린다. **픽셀 우선, 뷰포트를 못 찾으면 인덱스 근사로 폴백.**
  //   세로: `.ag-body-viewport`(overflow-y:auto) — AG Grid 내부 setVerticalScrollPosition도 이 요소의
  //         scrollTop을 직접 쓴다. 가로: `.ag-body-horizontal-scroll-viewport`(overflow-x:scroll).
  // 보정으로 행 수가 줄어 예전 위치가 최대치를 넘으면 브라우저가 알아서 끝까지로 맞춰준다.
  const restoreScroll = (pending) => {
    const api = gridRef.current?.api;
    if (!api) return;

    const box = gridBoxRef.current;
    const vertical = box?.querySelector(".ag-body-viewport");
    const horizontal = box?.querySelector(".ag-body-horizontal-scroll-viewport");

    if (vertical && pending.top != null) vertical.scrollTop = pending.top;
    else if (pending.rowIndex >= 0) api.ensureIndexVisible(pending.rowIndex, "top");

    if (horizontal && pending.left != null) horizontal.scrollLeft = pending.left;
    else if (pending.colId) api.ensureColumnVisible(pending.colId, "start");

    // 위치가 잡힌 **뒤에** 로딩 오버레이를 걷는다 — 먼저 걷으면 남은 미세한 이동이 그대로 보인다.
    api.setGridOption("loading", false);
  };

  // 예약된 스크롤 복원을 새 rowData가 반영된 뒤에 소비한다(reArrange의 pendingScrollRef 주석 참고).
  // 조회 등 다른 경로로 rowData가 바뀔 때는 예약이 없으므로 아무 일도 하지 않는다 — 그때는 맨 위에서
  // 시작하는 게 맞다.
  const onRowDataUpdated = () => {
    const pending = pendingScrollRef.current;
    if (!pending) return;
    pendingScrollRef.current = null;

    // 이 이벤트 시점엔 행이 막 붙어 스크롤 영역(scrollHeight)이 아직 갱신 전일 수 있다. 한 프레임
    // 뒤에 넣으면 새 높이가 반영된 상태라 값이 잘리지 않는다.
    requestAnimationFrame(() => restoreScroll(pending));
  };

  // TODO(MIG-T2): [adjustmentgrid3/AdjustmentGrid (5109)] 병합된 자원 코드가 위로 스크롤되면 안 보이는 문제 미해결.
  //   old: 해당 없음(old도 병합 셀이라 같은 한계였다)
  //   사유: AG Grid에 병합 셀 값을 고정하는 기능이 없고(sticky는 컬럼 그룹 헤더·행 그룹 전용),
  //         CSS `position: sticky`도 `.ag-cell`의 overflow와 행의 transform 배치에 막혀 동작하지 않는다.
  //         `onBodyScroll` + `getFirstDisplayedRowIndex`로 "현재 보이는 자원"을 그리드 위 띠에 표시하는
  //         방식을 만들어 봤으나, **다른 화면에 없는 UI라 통일성이 깨져** 되돌렸다(2026-08-25 사용자 판단).
  //         지금은 자원 경계선 + 자원 코드 칩으로만 구분한다.

  // 버전이 바뀌면 조회 구간을 다시 계산한다(old setVersionPeriod). 그룹을 넘나드는 로직이라
  // PeriodSearchGroup이 아니라 화면이 소유한다(fp-report-screen-guide.md §A-1b).
  useEffect(() => {
    const version = versionState.selectedVersion;
    if (!version?.startTs) {
      setPeriodState(prev => (prev.startDate || prev.endDate ? { ...prev, startDate: null, endDate: null } : prev));
      return;
    }

    const startDate = new Date(version.startTs);
    setPeriodState(prev => ({ ...prev, startDate, endDate: addDays(startDate, SEARCH_ZONE_DAYS) }));
    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.selectedVersion]);

  // planScope가 바뀌면 자원 목록과 대체 자원 맵을 다시 받는다(old handleChange의 planScope 분기).
  useEffect(() => {
    retrieveResources(versionState.planScope);
    retrieveAltResources(versionState.planScope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.planScope]);

  // 선택형 조건(버전 plant/기간)이 바뀌면 값이 준비되는 즉시 재조회한다. 자원 멀티셀렉트는
  // old처럼 조회 버튼으로만 커밋한다(선택 하나 바꿀 때마다 조회가 나가면 안 된다).
  useEffect(() => {
    const { versionState: version, periodState: period } = searchStateRef.current;
    if (!version.planScope || !version.versionCd) return;
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) return;

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodState.startDate, periodState.endDate, periodState.isEntirePlanPeriod, versionState.plantCd]);

  // 계획범위를 바꿨는데 버전이 빈 값으로 리셋된 경우 — 이전 조회 결과를 그대로 두지 않는다(§A-1b).
  useEffect(() => {
    if (versionState.versionCd) return;

    debouncedRetrieveRef.current.cancel();

    const api = gridRef.current?.api;
    if (api) {
      GridUtils.setColumnDefs({ api, columnDefs: fixedColumnDefs, viewName, initState: false });
      applyColumnOrder(api, fixedColumnDefs);
    }
    bucketColIdsRef.current = [];
    clearCutState();
    rememberGroupOrder(null);
    setRowData([]);
    // 하단 두 탭은 선택한 작업이 기준이라 결과를 비울 때 같이 지운다(지우지 않으면 사라진 행의
    // 작업으로 계속 조회한다).
    setSelectedActivity(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd]);

  return (
    <>
      <FilterContainer>
        <VersionSearchGroup
          value={versionState}
          onChange={(partial) => setVersionState(prev => ({ ...prev, ...partial }))}
        />
        {/* old useBucket={false} — 이 화면은 서버가 BUCKET_DAY로 고정한다(AdjustmentGridService). */}
        <PeriodSearchGroup
          value={periodState}
          onChange={(partial) => setPeriodState(prev => ({ ...prev, ...partial }))}
          useBucket={false}
        />
        {/* old InputField(type="multiSelect", resourceCd) — 자원 간트(ResourceGantt)와 같은 구성.
            선택을 하나 바꿀 때마다 조회가 나가면 안 되므로 드롭다운을 닫는 시점에만 커밋한다
            (old는 별도 조회 버튼으로 커밋했지만 new엔 그 자리에 쓸 공용 버튼 컴포넌트가 없고,
             다른 선택형 조건은 전부 즉시 조회이므로 "닫을 때 1회"가 두 관례의 접점이다). */}
        <Autocomplete
          multiple
          size="small"
          disableCloseOnSelect
          disableClearable
          options={resourceOptions}
          getOptionLabel={(option) => option?.label ?? ""}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          value={selectedResourceOptions}
          onChange={(_, options) => setResourceDraft(options)}
          onClose={commitResourceSelection}
          PaperComponent={renderResourcePaper}
          // 선택이 100개까지 가므로 태그(Chip)를 쌓지 않고 한 줄 요약만 보여준다.
          renderTags={(value) => value.length === 0 ? null : (
            <Box component="span" sx={{ pl: 0.75, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {allResourcesSelected
                ? t("ALL")
                : value.length === 1 ? value[0].label : `${value[0].label} +${value.length - 1}`}
            </Box>
          )}
          renderOption={(props, option, { selected }) => (
            <Box component="li" {...props} key={option.value} sx={{ gap: 0.5 }}>
              <Checkbox size="small" checked={selected} sx={{ p: 0.25 }} />
              {option.label}
            </Box>
          )}
          renderInput={(params) => <TextField {...params} label={t("FP_RESOURCE")} />}
          sx={{ ml: 1.5, minWidth: 250 }}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showDetailTooltip}
                onChange={(e) => setShowDetailTooltip(e.target.checked)}
              />
            }
            label={t("FP_SHOW_DETAIL_TOOLTIP")}
            slotProps={{ typography: { fontSize: "0.875rem" } }}
          />
          <DownloadButton
            onClick={() => gridRef.current?.api?.exportDataAsExcel({
              fileName: `${t(title)}.xlsx`,
              sheetName: t(title),
              // old excelExportOptions.objectCallback — 작업 객체 셀을 "품목코드\n수량"으로 내보낸다.
              processCellCallback: (params) => {
                const value = params.value;
                if (!value || typeof value !== "object") return value ?? "";
                return `${value.itemCd ?? ""}\n${numberUtil.formatNumber(value.qty)}`;
              }
            })}
          />
        </Box>
      </FilterContainer>
      {/* 높이는 flex 체인으로 만든다(#tab-body가 display:flex/column/height:100%) — §A-2대로
          `grid-area`(overflow:auto + height:100%)는 붙이지 않는다. 붙이면 AG Grid 자체 스크롤과
          겹쳐 스크롤 컨테이너가 두 겹이 된다. 하단 상세가 붙으면서 아래 여백(mb)은 분할 바의
          여백(DETAIL_GUTTER_MARGIN)이 대신한다. */}
      {/* 상세 패널이 우측 오버레이(absolute)라 이 영역이 기준 컨테이너가 된다 — 그래서
          `position: relative`가 필요하다. 그리드 위에 얹히므로 열어도 컬럼 폭이 바뀌지 않는다
          (자원 간트가 Drawer를 쓰지 않은 이유와 같다 — GanttDetailPanel 머리말 참고). */}
      <Box
        ref={gridBoxRef}
        className="ag-theme-balham"
        sx={{
          flex: 1, minHeight: 0, mt: 1, position: "relative",
          // 셀 툴팁 그림자 — **카드(ActivityTooltip)가 아니라 AG Grid가 만든 래퍼 div에 건다.**
          //
          // 실측한 DOM(2026-08-25, AG Grid 35):
          //   <div class="ag-theme-balham ag-popup">                     ← 팝업 컨테이너
          //     └ <div class="ag-react-container ag-tooltip-custom       ← ★ 여기에 그림자를 건다
          //                  ag-popup-child ag-tooltip-animate …">
          //         └ <Paper>                                           ← ActivityTooltip 카드
          //
          // 래퍼가 `ag-react-container`(AG Grid React가 컴포넌트를 감싸려고 만든 div)라, **카드 쪽에
          // box-shadow든 filter든 걸면 화면에 나오지 않는다** — 둘 다 실측으로 확인했다. 간트 툴팁은
          // MUI Popper가 body에 직접 포털로 붙어 이 래퍼가 없어서 카드의 box-shadow가 그냥 동작한다.
          // 래퍼는 그리드 DOM 안에 있으므로 여기서 셀렉터로 닿는다.
          // 라운드를 카드와 같은 값으로 맞춰야 drop-shadow가 카드 모서리를 따라 그려진다.
          "& .ag-tooltip-custom": {
            borderRadius: UI.tipCardRadius,
            filter: UI.tipCardDropShadow
          },
          // 자원이 바뀌는 행의 굵은 경계선(신호 ①). **행이 아니라 셀에 건다** — AG Grid 행은 절대배치라
          // 행에 border를 주면 고정 rowHeight와 겹쳐 내용이 밀린다. 셀 border는 인라인이 아니라 CSS라
          // 선택 하이라이트(배경)와도 공존한다.
          [`& .${GROUP_START_CLASS} .ag-cell`]: { borderTop: GROUP_BORDER }
        }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          {...defaultGridMemo}
          onGridReady={onGridReady}
          onRowDataUpdated={onRowDataUpdated}
          onCutStart={handleCutStart}
          onCellClicked={handleCellClicked}
          processDataFromClipboard={handlePasteFromClipboard}
          getContextMenuItems={getContextMenuItems}
        />
        <GanttDetailPanel
          open={activityDetailOpen}
          sections={activityDetailSections}
          openSections={openDetailSections}
          onToggleSection={toggleDetailSection}
          onClose={() => setActivityDetailOpen(false)}
          loading={activityDetailLoading}
          loadingMessage={t("MSG_Loading")}
          t={t}
        />
      </Box>

      {/* 분할 바 — 접혀 있어도 남겨 둔다(드래그로 바로 열 수 있게). 두께·색·여백은 자원 간트와 같게
          맞춘다(공용 SplitterContainer 기본값 계열). */}
      <Box
        onMouseDown={handleDetailDragStart}
        sx={{
          flex: `0 0 ${DETAIL_GUTTER}px`, my: `${DETAIL_GUTTER_MARGIN}px`,
          backgroundColor: "#F5F5F5", cursor: "row-resize",
          display: "flex", alignItems: "center", justifyContent: "center",
          "&::after": {
            content: '""', width: 24, height: 2, borderTop: "1px dotted", borderBottom: "1px dotted",
            borderColor: "grey.500"
          }
        }}
      />

      {/* 하단 영역 = 탭 바 + 선택한 탭의 그리드. 탭 바가 분할 바 아래(이 영역 안)에 있다 —
          old DetailsSplitArea의 구성 그대로다. 접힘 = 이 영역이 탭 바 높이만 남은 상태. */}
      <Box sx={{ display: "flex", flexDirection: "column", flex: `0 0 ${detailHeight}px`, minHeight: 0, overflow: "hidden" }}>
        <Box sx={{ display: "flex", alignItems: "center", flex: `0 0 ${DETAIL_TAB_BAR_HEIGHT}px` }}>
          <Tooltip title={detailCollapsed ? t("FP_EXPAND") : t("FP_COLLAPSE")}>
            <IconButton size="small" onClick={toggleDetail} sx={{ mx: 0.5 }}>
              {detailCollapsed ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tabs
            value={detailTab}
            onChange={(_, value) => setDetailTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: DETAIL_TAB_BAR_HEIGHT }}
          >
            {DETAIL_TABS.map(tab => (
              <Tab
                key={tab.key}
                value={tab.key}
                label={t(tab.labelKey)}
                sx={{ minHeight: DETAIL_TAB_BAR_HEIGHT, py: 0.75, fontSize: "0.875rem" }}
              />
            ))}
          </Tabs>
        </Box>

        {/* 탭 내용 — 접혀 있으면 렌더하지 않고, **활성 탭 하나만 마운트한다**(접힌 동안 AG Grid가
            0px로 초기화돼 펼칠 때 헤더가 안 그려지는 것을 피한다 — 자원 간트와 같은 이유). */}
        <Box
          sx={{
            flex: "1 1 0", minHeight: 0, overflow: "hidden",
            pt: detailCollapsed ? 0 : `${DETAIL_CONTENT_GAP}px`
          }}
        >
          {!detailCollapsed ? (
            <>
              {detailTab === "plan" && (
                <ResourcePlanDetailTab rows={resourcePlanRows} viewName={`${viewName}_PLAN_DETAIL`} t={t} />
              )}
              {detailTab === "psi" && (
                <PsiTab
                  active
                  activity={selectedActivity}
                  versionState={versionState}
                  viewName={`${viewName}_PSI`}
                  t={t}
                />
              )}
            </>
          ) : null}
        </Box>
      </Box>
      <AdjustQtyPopup
        type={qtyPopup.type}
        open={qtyPopup.open}
        activity={qtyPopup.activity}
        onClose={() => setQtyPopup(prev => ({ ...prev, open: false }))}
        onConfirm={(data) => setPendingQtyAdjust({ type: qtyPopup.type, activity: qtyPopup.activity, data })}
      />
      {/* 수량 보정/분할 확인 — 공용 Dialog(title="confirm" → t("confirmMessage"))를 쓰는
          FP 화면 관례(master/Route.js)를 따른다. onHandler는 확인 시 "CONFIRM"을 넘겨준다. */}
      <Dialog
        open={!!pendingQtyAdjust}
        onClose={() => setPendingQtyAdjust(null)}
        title="confirm"
        content={t("FP_MSG_ADJUST")}
        onHandler={(response) => {
          if (response === "CONFIRM") applyQtyAdjust(pendingQtyAdjust);
        }}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(AdjustmentGrid);
